import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Building2, Loader2, Search, ChevronRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EnhancedOrganizationHierarchy } from "@/components/organization/EnhancedOrganizationHierarchy";
import { VisualOrganizationHierarchy } from "@/components/organization/VisualOrganizationHierarchy";
import { fetchOrganizations } from "@/services/organization/organizationService";
import { useToast } from "@/hooks/use-toast";
import { Tables } from "@/integrations/supabase/types";

type Organization = Tables<"organizations">;

export default function OrganizationHierarchyVisualizer() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(organizationId || null);
  const [rootOrganizations, setRootOrganizations] = useState<Organization[]>([]);
  
  useEffect(() => {
    loadOrganizations();
  }, []);
  
  useEffect(() => {
    if (organizationId && organizationId !== selectedOrganizationId) {
      setSelectedOrganizationId(organizationId);
    }
  }, [organizationId]);
  
  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const data = await fetchOrganizations();
      setOrganizations(data || []);
      
      // Identify root organizations (those without a parent)
      const roots = data?.filter(org => !org.parent_id) || [];
      setRootOrganizations(roots);
      
      // If no organization is selected, default to the first root
      if (!selectedOrganizationId && roots.length > 0) {
        setSelectedOrganizationId(roots[0].id);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load organizations",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleOrganizationSelect = (orgId: string) => {
    setSelectedOrganizationId(orgId);
    navigate(`/organization-hierarchy/${orgId}`);
  };
  
  const filteredOrganizations = searchQuery
    ? organizations.filter(org => 
        org.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : [];
  
  const selectedOrganization = organizations.find(org => org.id === selectedOrganizationId);
  
  return (
    <div className="container mx-auto py-6 max-w-7xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Organization Hierarchy</h1>
          <p className="text-muted-foreground">
            View and explore organizational relationships and structure
          </p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left sidebar */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Organizations</CardTitle>
              <CardDescription>
                Select an organization to view its hierarchy
              </CardDescription>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search organizations..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-primary mr-2" />
                  <span>Loading organizations...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {searchQuery ? (
                    filteredOrganizations.length > 0 ? (
                      <div className="space-y-2">
                        <h3 className="text-sm font-medium">Search Results</h3>
                        {filteredOrganizations.map(org => (
                          <Button
                            key={org.id}
                            variant="ghost"
                            className={`w-full justify-start ${
                              selectedOrganizationId === org.id ? 'bg-accent text-accent-foreground' : ''
                            }`}
                            onClick={() => handleOrganizationSelect(org.id)}
                          >
                            <Building2 className="h-4 w-4 mr-2" />
                            {org.name}
                          </Button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-muted-foreground">
                        No organizations found
                      </div>
                    )
                  ) : (
                    <>
                      <div className="space-y-2">
                        <h3 className="text-sm font-medium">Root Organizations</h3>
                        {rootOrganizations.length > 0 ? (
                          rootOrganizations.map(org => (
                            <Button
                              key={org.id}
                              variant="ghost"
                              className={`w-full justify-start ${
                                selectedOrganizationId === org.id ? 'bg-accent text-accent-foreground' : ''
                              }`}
                              onClick={() => handleOrganizationSelect(org.id)}
                            >
                              <Building2 className="h-4 w-4 mr-2" />
                              {org.name}
                            </Button>
                          ))
                        ) : (
                          <div className="text-center py-4 text-muted-foreground">
                            No root organizations found
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-sm font-medium">Recent</h3>
                        {/* This would show recently viewed organizations */}
                        <div className="text-center py-4 text-muted-foreground">
                          No recent organizations
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Main content */}
        <div className="lg:col-span-3">
          {loading ? (
            <Card>
              <CardContent className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
                <span>Loading organization hierarchy...</span>
              </CardContent>
            </Card>
          ) : selectedOrganizationId ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>
                      {selectedOrganization ? selectedOrganization.name : 'Organization Hierarchy'}
                    </CardTitle>
                    <CardDescription>
                      Hierarchical view and analytics
                    </CardDescription>
                  </div>
                  <Button 
                    variant="outline"
                    onClick={() => navigate(`/organizations/${selectedOrganizationId}`)}
                  >
                    View Details
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="visual">
                  <TabsList className="mb-4">
                    <TabsTrigger value="visual">Visual Chart</TabsTrigger>
                    <TabsTrigger value="hierarchy">Tree View</TabsTrigger>
                    <TabsTrigger value="metrics">Metrics &amp; Analytics</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="visual">
                    <VisualOrganizationHierarchy
                      organizationId={selectedOrganizationId}
                      className="h-[700px]"
                    />
                  </TabsContent>
                  
                  <TabsContent value="hierarchy">
                    <EnhancedOrganizationHierarchy
                      organizationId={selectedOrganizationId}
                      showControls={true}
                      showSearch={true}
                      showMetrics={true}
                      initialView="tree"
                      defaultDepth={3}
                      size="lg"
                      initiallyExpanded={true}
                    />
                  </TabsContent>
                  
                  <TabsContent value="metrics">
                    <EnhancedOrganizationHierarchy
                      organizationId={selectedOrganizationId}
                      showControls={false}
                      showSearch={false}
                      showMetrics={true}
                      initialView="metrics"
                      defaultDepth={3}
                      size="lg"
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center p-12">
                <Building2 className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-xl font-medium mb-2">No Organization Selected</h3>
                <p className="text-muted-foreground text-center mb-6">
                  Select an organization from the sidebar to view its hierarchy
                </p>
                {rootOrganizations.length > 0 && (
                  <Button 
                    onClick={() => handleOrganizationSelect(rootOrganizations[0].id)}
                  >
                    View Root Organization
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}